import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import InputField from '../inputField/InputField';
import Button from '../button/Button';
import Modal from '../modal/Modal';
import MultiSelectDropdown from '../multiSelectDropdown/MultiSelectDropdown';
import { RESPONSE } from '../../js/_constants';
import { type ModuleType, type Playlist, type Device } from '../../api/types/index';
import { type DropdownOptionType } from '../multiSelectDropdown/MultiSelectDropdown';

type EditDeviceTypes = {
    deviceDescription: string;
    deviceModuleTypeIds: Device['moduleTypes'] | [];
    deviceTitle: string;
    incompatiblePlaylists: Playlist[];
    isSaving?: boolean;
    moduleTypeOptions: ModuleType[];
    onCloseEditModalClick: () => void;
    onDescChange: (param: string) => void;
    onModuleTypeSelection: (param: DropdownOptionType[]) => void;
    onSaveEditClick: () => void;
    onTitleChange: (param: string) => void;
};

const EditDevice = ({
    deviceDescription,
    deviceModuleTypeIds,
    deviceTitle,
    incompatiblePlaylists,
    isSaving,
    moduleTypeOptions,
    onCloseEditModalClick,
    onDescChange,
    onModuleTypeSelection,
    onSaveEditClick,
    onTitleChange
}: EditDeviceTypes) => {
    const { t } = useTranslation();

    const [showConfirmation, setShowConfirmation] = useState(false);
    const onOpenDeleteConfirm = () => setShowConfirmation(true);
    const onCloseDeleteConfirm = () => setShowConfirmation(false);
    const ref = useRef(null);

    return (
        <div className={cx('edit-device', {
            'is-disabled': isSaving
        })} ref={ref}>
            <div className="edit">
                <div className="inputs">
                    <div className="left-inputs">
                        <InputField
                            extraClass="p-title"
                            label={t('device_title')}
                            name="title"
                            inputType="text"
                            value={deviceTitle}
                            onValueChangeHandler={onTitleChange}
                        />
                        <MultiSelectDropdown
                            className="p-module"
                            name={t('playlist_module_type')}
                            label={t('playlist_module_type')}
                            options={moduleTypeOptions.map(moduleType => ({
                                value: moduleType[RESPONSE.ID],
                                label: moduleType.title
                            }))}
                            onChange={onModuleTypeSelection}
                            defaultValue={deviceModuleTypeIds.map(
                                moduleType => ({
                                    value: moduleType[RESPONSE.ID],
                                    label: moduleType.title
                                })
                            )}
                            isFilterable
                        />
                    </div>
                    <InputField
                        extraClass="edit-device__description-textarea"
                        label={t('device_detail_edit_description_title')}
                        name="description"
                        inputType="textarea"
                        value={deviceDescription}
                        placeholder={t('device_detail_edit_description')}
                        onValueChangeHandler={onDescChange}
                    />
                </div>
                <div className="b-modal-action-buttons">
                    <Button
                        secondaryButton
                        text={t('cancel')}
                        clickHandler={onCloseEditModalClick}
                        disabled={isSaving}
                    />
                    <Button
                        text={t('save')}
                        clickHandler={
                            incompatiblePlaylists.length > 0
                                ? onOpenDeleteConfirm
                                : onSaveEditClick
                        }
                        isLoading={isSaving}
                    />
                </div>
                <Modal
                    className="confirmation-modal"
                    show={showConfirmation}
                    onClose={onCloseDeleteConfirm}
                    onCancel={onCloseDeleteConfirm}
                    onAction={onSaveEditClick}
                    localeAction={t('apply')}
                    localeCancel={t('discard')}
                >
                    <h4>{t('device_change_module')}</h4>
                    <ul className="playlists-list">
                        {incompatiblePlaylists.map(entry => (
                            <li className="playlist-title" key={entry.title}>
                                {entry.title}
                            </li>
                        ))}
                    </ul>
                </Modal>
            </div>
        </div>
    );
};

export default EditDevice;
